import { routes, type RouteName } from "@malfak/contracts";
import {
  queryOptions,
  useMutation,
  useQuery,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, getCurrentOrgId, type ApiArgs, type ApiCallArgs, type ApiResponse } from "./client";
import type { ApiError } from "./errors";

/**
 * TanStack Query on top of the typed client. Query keys are
 * `[routeName, orgId, params, query]`, so `invalidate("inbox")` hits every inbox page
 * and filter combination at once.
 */
export function apiKey<K extends RouteName>(name: K, args?: Partial<ApiArgs<K>>): QueryKey {
  const a = (args ?? {}) as { params?: unknown; query?: unknown };
  const orgScoped = routes[name].path.includes(":orgId");
  return [name, orgScoped ? getCurrentOrgId() : null, a.params ?? null, a.query ?? null];
}

export function apiQuery<K extends RouteName>(name: K, ...rest: ApiCallArgs<K>) {
  const args = rest[0] as ApiArgs<K> | undefined;
  return queryOptions<ApiResponse<K>, ApiError>({
    queryKey: apiKey(name, args),
    queryFn: ({ signal }) => (api as (n: K, a: ApiArgs<K>) => Promise<ApiResponse<K>>)(name, { ...(args ?? {}), signal } as ApiArgs<K>),
  });
}

type QueryOpts<K extends RouteName, TData> = Omit<UseQueryOptions<ApiResponse<K>, ApiError, TData>, "queryKey" | "queryFn">;

export function useApiQuery<K extends RouteName, TData = ApiResponse<K>>(name: K, args?: ApiArgs<K>, options?: QueryOpts<K, TData>) {
  return useQuery<ApiResponse<K>, ApiError, TData>({
    ...(apiQuery as (n: K, a?: ApiArgs<K>) => ReturnType<typeof apiQuery<K>>)(name, args),
    ...(options ?? {}),
  } as UseQueryOptions<ApiResponse<K>, ApiError, TData>);
}

export function useApiMutation<K extends RouteName, TContext = unknown>(
  name: K,
  options?: Omit<UseMutationOptions<ApiResponse<K>, ApiError, ApiArgs<K>, TContext>, "mutationFn">,
) {
  return useMutation<ApiResponse<K>, ApiError, ApiArgs<K>, TContext>({
    mutationKey: [name],
    mutationFn: (args) => (api as (n: K, a: ApiArgs<K>) => Promise<ApiResponse<K>>)(name, args),
    ...(options ?? {}),
  });
}
